import React from 'react';
import { Space, Button, Dropdown, Tooltip, Row, Modal, notification } from 'antd';
import { DownloadOutlined, ReloadOutlined, RobotOutlined, UploadOutlined } from '@ant-design/icons';
import ShopifyProductSelect from '../../ShopifyProduct/ShopifyProductSelect';
import { useAppSelector } from '../../../store/hooks';
import { analyzeShopifyProductImages } from '../../../api/analyzeShopifyProductImage';

interface TableActionsProps {
    selectedRowKeys: React.Key[];
    setShopifyUploadApprovedContentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleReloadShopifyProductImages: () => void
}

const TableActions: React.FC<TableActionsProps> = ({ selectedRowKeys, setShopifyUploadApprovedContentModalOpen, handleReloadShopifyProductImages }) => {
    const selectedShopifyShop = useAppSelector((state) => state.shopifyShops.selectedShopifyShop)

    // TODO: Set proper notifications
    const handleAnalyzeShopifyProductImages = async () => {
        if (selectedShopifyShop) {
            return await analyzeShopifyProductImages(selectedShopifyShop.id)
        }
    }

    const handleMenuClick = (action: string, type: React.Key) => {
        if (action === 'download') {
            if (type === 'all') {
                console.log('Download All');
                // Implement download all logic
            } else if (type === 'selected') {
                console.log('Download Selected');
                // Implement download selected logic
            }
        }
    };

    const downloadMenu: {
        items: Array<{ key: React.Key; label: string; disabled?: boolean }>;
        onClick: ({ key }: { key: React.Key }) => void;
    } = {
        items: [
            { key: 'all', label: 'Download All' },
            { key: 'selected', label: `Download Selected (${selectedRowKeys.length})`, disabled: selectedRowKeys.length === 0 },
        ],
        onClick: ({ key }) => handleMenuClick('download', key),
    };

    const handleGenerateImageContent = () => {
        if (!selectedShopifyShop) return;
        const imageCount = selectedShopifyShop.stats?.imageCount ?? selectedShopifyShop.iamgeCount ?? 0;
        Modal.confirm({
            title: 'Generate Content for All Images',
            content: `Are you sure you want to generate content for all ${imageCount} images? This may take a while and consume your quota.`,
            okText: 'Yes, generate',
            cancelText: 'Cancel',
            onOk: async () => {
                const result = await handleAnalyzeShopifyProductImages();
                const scheduledCount = result?.scheduledCount ?? 0;
                const quotaReached = result?.quotaReached ?? false;

                if (scheduledCount === 0) {
                    notification.warning({
                        message: 'No generations started',
                        description: result?.message ?? 'No new Shopify image generations were scheduled.',
                    });
                    return;
                }

                if (quotaReached) {
                    notification.warning({
                        message: 'Quota reached',
                        description: `Started ${scheduledCount} of ${imageCount} Shopify image generations before the company quota was exhausted.`,
                    });
                    return;
                }

                notification.success({
                    message: 'Generation started',
                    description: `Started ${scheduledCount} Shopify image generations.`,
                });
            },
        });
    };

    return (
        <Row justify="space-between" align="middle">
            <Space>
                <ShopifyProductSelect />
            </Space>
            <Space size="small">
                <Space.Compact>
                    <Tooltip title="Upload Approved Content to Shopify">
                        <Button
                            type="default"
                            icon={<UploadOutlined />}
                            onClick={() => setShopifyUploadApprovedContentModalOpen(true)}
                        />
                    </Tooltip>
                    <Tooltip title="Download">
                        <Dropdown menu={downloadMenu} trigger={['click']}>
                            <Button
                                type="default"
                                disabled={true}
                                icon={<DownloadOutlined />}
                            />
                        </Dropdown>
                    </Tooltip>
                </Space.Compact>
                <Tooltip title="Generate Content">
                    <Button
                        type="primary"
                        icon={<RobotOutlined />}
                        onClick={handleGenerateImageContent}
                    />
                </Tooltip>
                <Tooltip title="Refresh">
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={handleReloadShopifyProductImages}
                    />
                </Tooltip>
                {/* TODO: Recreate Dropdown */}
                {/* <Tooltip title="Analyze">
                                <Dropdown menu={analyzeMenu} trigger={['click']}>
                                    <Button
                                        disabled={true}
                                        type="primary"
                                        icon={<BarChartOutlined />}
                                    />
                                </Dropdown>
                            </Tooltip> */}
            </Space>
        </Row>
    );
};

export default TableActions;
